/* eslint-disable @typescript-eslint/no-unused-vars */
import { css, Theme, useTheme } from '@emotion/react'
import { FC, useMemo } from 'react'
import { Form, Field, useField } from 'react-final-form'

import ConfirmText from '~/form/components/atoms/ConfirmText'
import FieldError from '~/form/components/atoms/FieldError'
import FieldLabel, { FieldLabelProps } from '~/form/components/atoms/FieldLabel'
import FieldWrapper from '~/form/components/atoms/FieldWrapper'
import HelperText from '~/form/components/atoms/HelperText'
import useValidationErrorClassName from '~/form/hooks/useValidationErrorClassName'

import IconArrow from '../atoms/icons/IconArrow'

export type Props = {
  name: string
  isConfirm: boolean
  helperText?: string
  options: string[]
} & FieldLabelProps

const SelectMenu: FC<Props> = ({
  name,
  label,
  required,
  htmlFor,
  isConfirm,
  helperText,
  options,
}) => {
  const { input, meta } = useField(name)

  const theme = useTheme()

  const isShowError = useMemo(
    () => meta.error && meta.touched,
    [meta.error, meta.touched]
  )

  const wrapperClassName = useValidationErrorClassName(isShowError)

  return (
    <div className={wrapperClassName}>
      <FieldWrapper>
        <FieldLabel
          label={label}
          required={required}
          htmlFor={htmlFor}
          isShowError={isShowError}
        />
        <div css={fieldDetailGroupStyle()}>
          {isConfirm ? (
            <ConfirmText value={input.value} />
          ) : (
            <div
              css={[selectStyle(theme), isShowError && selectErrorStyle(theme)]}
            >
              <Field name={name} component='select'>
                <option value=''>選択してください</option>
                {options.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </Field>
              <IconArrow css={selectArrowStyle} />
            </div>
          )}
          <FieldError isShowError={isShowError} error={meta.error} />
          <HelperText text={helperText} />
        </div>
      </FieldWrapper>
    </div>
  )
}

export default SelectMenu

const fieldDetailGroupStyle = () => css`
  display: grid;
  row-gap: 5px;

  @media (max-width: 767px) {
    row-gap: 7px;
  }
`

const selectStyle = (theme: Theme) => css`
  position: relative;
  font-size: 14px;

  select {
    padding: 16px 30px 16px 13px;
    border: 2px solid transparent;
    border-radius: 10px;
    color: ${theme.color.black};
    background-color: #fff;
    width: 100%;
    font-size: inherit;
    line-height: calc(24 / 14);

    @media (max-width: 767px) {
      padding-block: 11px;
    }

    &:focus {
      transition: box-shadow 400ms cubic-bezier(0.215, 0.61, 0.355, 1);
      box-shadow: 0 0 0 2px ${theme.color.yellow};
    }
  }
`

const selectArrowStyle = css`
  position: absolute;
  top: 50%;
  right: 25px;
  transform: translateY(-50%);
  pointer-events: none;

  @media (max-width: 767px) {
    right: 18px;
  }
`

const selectErrorStyle = (theme: Theme) => css`
  color: ${theme.color.red};

  select {
    border-color: ${theme.color.red};
  }
`
